import { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Crown, Mail, Lock, User, AlertCircle, Loader2, IdCard, Phone, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useBookingStatus } from "../contexts/BookingStatusContext";
import { motion } from "motion/react";
import { ForgotPassword } from "./ForgotPassword";
import { ResendVerification } from "./ResendVerification";
import { AuthDialogHeader } from "./AuthDialogHeader";

interface LoginSignupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function LoginSignup({ open, onOpenChange, onSuccess }: LoginSignupProps) {
  const { login, signup } = useAuth();
  const { bookingEnabled, bookingClosedMessage } = useBookingStatus();
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhoneNumber, setSignupPhoneNumber] = useState("");
  const [signupGender, setSignupGender] = useState("male");
  const [signupRole, setSignupRole] = useState("student");
  const [signupStudentId, setSignupStudentId] = useState("");
  const [signupEmergencyContactName, setSignupEmergencyContactName] = useState("");
  const [signupEmergencyContactNumber, setSignupEmergencyContactNumber] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(loginEmail, loginPassword);
    setLoading(false);

    if (result.success) {
      setNeedsVerification(false);
      onSuccess();
      onOpenChange(false);
    } else {
      const errorMsg = result.error || "Invalid email or password";
      setError(errorMsg);
      // Check if the error is about email verification
      if (errorMsg.toLowerCase().includes('verify') && errorMsg.toLowerCase().includes('email')) {
        setNeedsVerification(true);
      } else {
        setNeedsVerification(false);
      }
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (signupPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (signupPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    const success = await signup(signupEmail, signupPassword, signupName, signupPhoneNumber, signupGender, signupRole, signupStudentId, signupEmergencyContactName, signupEmergencyContactNumber);
    setLoading(false);

    if (success) {
      // Don't call onSuccess() for signup - user needs to verify email first
      // Only close the dialog and show success message
      onOpenChange(false);
    } else {
      setError("Email already exists or signup failed. Please try again.");
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setShowResendVerification(false);
  };

  const handleResendClose = () => {
    setShowResendVerification(false);
    setNeedsVerification(false);
    setError("");
  };

  if (showForgotPassword) {
    return (
      <ForgotPassword
        open={open}
        onOpenChange={onOpenChange}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  if (showResendVerification) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] p-0">
          <AuthDialogHeader
            icon={Mail}
            title="Resend Verification"
            description="Request a new email verification link"
          />
          <div className="p-6">
            <ResendVerification onClose={handleResendClose} />
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-[#0f1f3d] hover:text-[#d4af37] transition-colors"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] p-0 overflow-hidden">
        <AuthDialogHeader
          icon={Crown}
          title="Welcome to Majesty Hostel"
          description="Login or create an account to book your room"
        />

        {!bookingEnabled && (
          <div role="alert" className="mx-6 mt-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">{bookingClosedMessage}</span>
            </div>
          </div>
        )}

        <Tabs defaultValue="login" className="p-6 overflow-y-auto flex-1">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          {error && (
            <motion.div
              role="alert"
              aria-live="assertive"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
              {needsVerification && (
                <button
                  type="button"
                  onClick={() => setShowResendVerification(true)}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline font-medium w-full text-left"
                >
                  Didn't receive the email? Click here to resend verification
                </button>
              )}
            </motion.div>
          )}

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your.email@example.com"
                    className="pl-10"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    id="login-password"
                    type={showLoginPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword((v) => !v)}
                    aria-label={showLoginPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-3 text-gray-400 hover:text-[#0f1f3d] transition-colors"
                  >
                    {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                variant="gold"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  "Login"
                )}
              </Button>

              <div className="text-center mt-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-[#0f1f3d] hover:text-[#d4af37] transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Ama Mensah"
                      className="pl-10"
                      value={signupName}
                      onChange={(e) => setSignupName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-gender">Gender</Label>
                  <Select value={signupGender} onValueChange={setSignupGender}>
                    <SelectTrigger id="signup-gender" className="w-full">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-role">I am a</Label>
                  <Select
                    value={signupRole}
                    onValueChange={(value) => {
                      setSignupRole(value);
                      if (value === "parent") setSignupStudentId("");
                    }}
                  >
                    <SelectTrigger id="signup-role" className="w-full">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="parent">Parent/Guardian</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {signupRole !== "parent" && (
                  <div className="space-y-2">
                    <Label htmlFor="signup-student-id">Student ID (Optional)</Label>
                    <div className="relative">
                      <IdCard className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <Input
                        id="signup-student-id"
                        type="text"
                        placeholder="Enter your student ID"
                        className="pl-10"
                        value={signupStudentId}
                        onChange={(e) => setSignupStudentId(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="your.email@example.com"
                      className="pl-10"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-phone-number">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="signup-phone-number"
                      type="tel"
                      placeholder="+233 XX XXX XXXX"
                      className="pl-10"
                      value={signupPhoneNumber}
                      onChange={(e) => setSignupPhoneNumber(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-emergency-contact-name">Emergency Contact Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="signup-emergency-contact-name"
                      type="text"
                      placeholder="Emergency contact full name"
                      className="pl-10"
                      value={signupEmergencyContactName}
                      onChange={(e) => setSignupEmergencyContactName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-emergency-contact-number">Emergency Contact Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="signup-emergency-contact-number"
                      type="tel"
                      placeholder="Emergency contact phone number"
                      className="pl-10"
                      value={signupEmergencyContactNumber}
                      onChange={(e) => setSignupEmergencyContactNumber(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="signup-password"
                      type={showSignupPassword ? "text" : "password"}
                      placeholder="Create a password (min. 6 characters)"
                      className="pl-10 pr-10"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupPassword((v) => !v)}
                      aria-label={showSignupPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-3 text-gray-400 hover:text-[#0f1f3d] transition-colors"
                    >
                      {showSignupPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-3 text-gray-400 hover:text-[#0f1f3d] transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="gold"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
